'use client'

import {
  useEffect,
  useRef,
  useState,
} from 'react'

import Image from 'next/image'
import {
  AnimatePresence,
  motion,
} from 'framer-motion'

import CinematicSceneFrame from './CinematicSceneFrame'
import {
  usePromanityExperienceContext,
} from '@/contexts/promanity/PromanityExperienceProvider'
import {
  CORE_BOOT_STAGE_EVENT,
  type CoreBootStage,
} from './coreBootEvents'
import { useBranding } from '@/hooks/useBranding'

type RoomBackgroundProps = {
  sellerId: number
}

const ROOM_BACKGROUND_OFF =
  '/images/promanity/room/promanity-room-off.webp'

const ROOM_BACKGROUND_GLITCH_OFF =
  '/images/promanity/room/promanity-room-glitch-off.webp'

const ROOM_BACKGROUND_READY =
  '/images/promanity/room/promanity-room-ready.webp'

const ROOM_BACKGROUND_ON =
  '/images/promanity/room/promanity-room-on.webp'

const ROOM_BACKGROUND_ORB_READY =
  '/images/promanity/room/promanity-room-orb-ready.webp'

const AIA_BACKGROUND_ON =
  '/images/promanity/room/central-console-on.webp'

const AIA_BACKGROUND_OFF =
  '/images/promanity/room/central-console-off.webp'

const WINDOW_BACKGROUND =
  '/images/promanity/room/window.webp'

const CONSOLE_SCREEN_ON =
  '/images/promanity/room/console-screen-on.webp'

const CONSOLE_SCREEN_OFF =
  '/images/promanity/room/console-screen-off.webp'

const CONSOLE_FOCUS =
  '/images/promanity/room/console-focus.webp'

const ROOM_GLITCH_AUDIO =
  '/audio/promanity/core/room-glitch.mp3'

const ORB_AWAKENING_AUDIO =
  '/audio/promanity/core/orb-awakening.mp3'

const GLITCH_OFF_DURATION_MS = 3000

const AIA_BOOT_DURATION_MS = 9000


export default function RoomBackground({
  sellerId,
}: RoomBackgroundProps) {
  const {
    room: { state },
  } = usePromanityExperienceContext()

  const { brand, loading: brandingLoading } =
    useBranding(sellerId)

  const [isDaytime, setIsDaytime] =
    useState<boolean | null>(null)

  useEffect(() => {
    const updateDayPeriod = () => {
      const hour = new Date().getHours()

      setIsDaytime(
        hour >= 6 && hour < 18
      )
    }

    updateDayPeriod()

    /*
    * Permite que cambie automáticamente si el usuario
    * mantiene abierta la habitación durante el cambio
    * entre día y noche.
    */
    const intervalId = window.setInterval(
      updateDayPeriod,
      60_000
    )

    return () => {
      window.clearInterval(intervalId)
    }
  }, [])

  const brandedWindowBackground =
    isDaytime === null
      ? null
      : isDaytime
        ? brand?.background?.light
        : brand?.background?.dark

  const [
    glitchFinished,
    setGlitchFinished,
  ] = useState(false)

  const [
    consoleBootFinished,
    setConsoleBootFinished,
  ] = useState(false)

  const [
    coreBootStage,
    setCoreBootStage,
  ] = useState<CoreBootStage>('idle')

  const playedAwakeningRef =
    useRef<string | null>(null)

  const glitchAudioRef =
    useRef<HTMLAudioElement | null>(null)

  const awakeningAudioRef =
    useRef<HTMLAudioElement | null>(null)

  const isOverview =
    state.currentScene === 'overview'

  const roomPhase =
    state.lifecycle.phase

  const genesisCompletedAt =
    state.lifecycle.genesisCompletedAt

  const showRoomReady =
    roomPhase === 'new_origin' &&
    state.lifecycle.windowActivated &&
    !state.lifecycle.windowOpened

  const isCoreDormant =
    roomPhase === 'core_dormant'

  const showRoomGlitchOff =
    isOverview &&
    isCoreDormant &&
    !glitchFinished

  const showOrbReadyIdle =
    isOverview &&
    isCoreDormant &&
    glitchFinished

  /*
   * ROOM ON permanente después de que el usuario
   * haya activado fases posteriores.
   *
   * Durante core_dormant, ROOM_ON solamente se usa
   * para el ritual de ocho segundos.
   */
  const showSteadyRoomOn =
    roomPhase === 'core_active' ||
    roomPhase === 'growth' ||
    roomPhase === 'conservation' ||
    roomPhase === 'awakening' ||
    roomPhase === 'awakened'

  const isAIACore =
    state.currentScene === 'aia'

  const isAIACoreBoot =
    isAIACore &&
    roomPhase === 'aia_boot'

  console.log({
    scene: state.currentScene,
    roomPhase,
    isAIACore,
    isAIACoreBoot,
  })

  const isConsoleScreen =
    state.currentScene === 'console' &&
    state.aiaView === 'screen'

  const isConsoleFocus =
    state.currentScene === 'console' &&
    state.aiaView === 'focus'

  const isWorkspace =
    state.currentScene === 'workspace'

  useEffect(() => {
    function handleCoreBootStage(
      event: Event
    ) {
      const customEvent =
        event as CustomEvent<CoreBootStage>

      setCoreBootStage(
        customEvent.detail
      )
    }

    window.addEventListener(
      CORE_BOOT_STAGE_EVENT,
      handleCoreBootStage
    )

    return () => {
      window.removeEventListener(
        CORE_BOOT_STAGE_EVENT,
        handleCoreBootStage
      )
    }
  }, [])

  useEffect(() => {
    if (!isAIACore) {
      setCoreBootStage('idle')
    }
  }, [isAIACore])

  useEffect(() => {
    /*
    * El glitch comienza cuando regresamos físicamente
    * al plano general después de Genesis.
    *
    * No usamos genesisCompletedAt porque ese tiempo
    * puede transcurrir mientras seguimos dentro de
    * la vista de la ventana.
    */
    if (
      !isOverview ||
      !isCoreDormant
    ) {
      setGlitchFinished(false)
      return
    }

    setGlitchFinished(false)

    const audio =
      new Audio(ROOM_GLITCH_AUDIO)

    audio.preload = 'auto'
    audio.volume = 0.9

    glitchAudioRef.current = audio

    void audio.play().catch(() => {
      // El navegador puede bloquear autoplay.
    })

    const timer =
      window.setTimeout(() => {
        setGlitchFinished(true)
      }, GLITCH_OFF_DURATION_MS)

    return () => {
      window.clearTimeout(timer)

      const audio =
        glitchAudioRef.current

      if (audio) {
        audio.pause()
        audio.currentTime = 0
        glitchAudioRef.current = null
      }
    }
  }, [
    isOverview,
    isCoreDormant,
  ])

  useEffect(() => {
    if (!isAIACoreBoot) {
      setConsoleBootFinished(false)
      return
    }

    setConsoleBootFinished(false)

    const timer =
      window.setTimeout(() => {
        setConsoleBootFinished(true)
      }, AIA_BOOT_DURATION_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [isAIACoreBoot])

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden bg-black">
      <AnimatePresence
        mode="sync"
        initial={false}
      >
        {isOverview && (
          <motion.div
            key="overview-background"
            initial={{
              opacity: 0,
              scale: 1.025,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              scale: 1.045,
            }}
            transition={{
              duration: 0.9,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute inset-0 overflow-hidden bg-black"
          >
            <CinematicSceneFrame
              sourceWidth={1536}
              sourceHeight={1024}
              fit="height"
              showEnvironment
            >
              <div className="promanity-scene-image absolute inset-0 z-10">
                {/* FONDO VISIBLE A TRAVÉS DE LA VENTANA */}
                <AnimatePresence mode="wait">
                  {!brandingLoading && brandedWindowBackground ? (
                    <motion.div
                      key={brandedWindowBackground}
                      className="absolute inset-0 z-0 overflow-hidden"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{
                        duration: 1.2,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                    >
                      <Image
                        src={brandedWindowBackground}
                        alt=""
                        fill
                        priority
                        sizes="1536px"
                        className="object-cover object-center !top-[-10%]"
                        draggable={false}
                        unoptimized
                      />

                      <div className="absolute inset-0 bg-black/20" />

                      <div
                        className="
                          absolute inset-0
                          bg-[radial-gradient(circle_at_center,transparent_20%,rgba(0,0,0,0.35)_100%)]
                        "
                      />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
                {/*
                * ROOM OFF
                *
                * Base permanente del Centro de Comando.
                */}

                <Image
                  src={ROOM_BACKGROUND_OFF}
                  alt=""
                  fill
                  priority
                  sizes="1536px"
                  className="object-contain"
                  draggable={false}
                  unoptimized
                />

                {/*
                * ROOM READY
                *
                * Atrae al usuario hacia la ventana
                * antes de comenzar el onboarding.
                */}

                <motion.div
                  className="absolute inset-0"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: showRoomReady
                      ? [0.18, 1, 0.18]
                      : 0,
                  }}
                  transition={
                    showRoomReady
                      ? {
                          opacity: {
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          },
                        }
                      : {
                          opacity: {
                            duration: 0.8,
                            ease: [0.22, 1, 0.36, 1],
                          },
                        }
                  }
                >
                  <Image
                    src={ROOM_BACKGROUND_READY}
                    alt=""
                    fill
                    priority
                    sizes="1536px"
                    className="object-contain"
                    draggable={false}
                    unoptimized
                  />
                </motion.div>

                {/*
                * ROOM GLITCH OFF
                */}

                <AnimatePresence>
                  {showRoomGlitchOff && (
                    <motion.div
                      key="room-glitch-off-sequence"
                      className="absolute inset-0"
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: [
                          0,
                          1,
                          0,
                          0.92,
                          0.08,
                          1,
                          0,
                          0,
                          0.84,
                          0.14,
                          1,
                          0.04,
                          0.96,
                          0,
                          0.72,
                          0,
                        ],
                      }}
                      exit={{
                        opacity: 0,
                      }}
                      transition={{
                        opacity: {
                          duration:
                            GLITCH_OFF_DURATION_MS /
                            1000,
                          times: [
                            0,
                            0.04,
                            0.09,
                            0.14,
                            0.2,
                            0.27,
                            0.34,
                            0.43,
                            0.5,
                            0.57,
                            0.64,
                            0.71,
                            0.79,
                            0.87,
                            0.94,
                            1,
                          ],
                          ease: 'linear',
                        },
                      }}
                    >
                      <Image
                        src={
                          ROOM_BACKGROUND_GLITCH_OFF
                        }
                        alt=""
                        fill
                        priority
                        sizes="1536px"
                        className="object-contain"
                        draggable={false}
                        unoptimized
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/*
                * ROOM ON
                *
                * Estado activo permanente.
                */}

                <motion.div
                  className="absolute inset-0"
                  initial={false}
                  animate={{
                    opacity: showSteadyRoomOn
                      ? 1
                      : 0,
                  }}
                  transition={{
                    opacity: {
                      duration: 1.2,
                      ease: [0.22, 1, 0.36, 1],
                    },
                  }}
                >
                  <Image
                    src={ROOM_BACKGROUND_ON}
                    alt=""
                    fill
                    priority
                    sizes="1536px"
                    className="object-contain"
                    draggable={false}
                    unoptimized
                  />
                </motion.div>

                {/*
                * CORE READY
                *
                * Pulso que invita a interactuar
                * con el Core de AIA.
                */}

                <motion.div
                  className="absolute inset-0"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: showOrbReadyIdle
                      ? [0, 1, 0]
                      : 0,
                  }}
                  transition={
                    showOrbReadyIdle
                      ? {
                          opacity: {
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          },
                        }
                      : {
                          opacity: {
                            duration: 0.5,
                            ease: [0.22, 1, 0.36, 1],
                          },
                        }
                  }
                >
                  <Image
                    src={ROOM_BACKGROUND_ORB_READY}
                    alt=""
                    fill
                    priority
                    sizes="1536px"
                    className="object-contain"
                    draggable={false}
                    unoptimized
                  />
                </motion.div>
              </div>
            </CinematicSceneFrame>
          </motion.div>
        )}

        {isAIACore && (
          <motion.div
            key="central-console-background"
            initial={{
              opacity: 0,
              scale: 1.08,
              filter: 'blur(10px)',
            }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
            }}
            exit={{
              opacity: 0,
              scale: 0.98,
              filter: 'blur(6px)',
            }}
            transition={{
              duration: 0.85,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute inset-0 overflow-hidden bg-black"
          >
            <CinematicSceneFrame
              sourceWidth={1536}
              sourceHeight={1024}
              fit="height"
              showEnvironment
            >
              <div className="promanity-scene-image">
                {/* CONSOLA APAGADA */}
                <Image
                  src={AIA_BACKGROUND_OFF}
                  alt=""
                  fill
                  priority
                  sizes="1536px"
                  className="object-contain"
                  draggable={false}
                  unoptimized
                />

                {/* CONSOLA ENCENDIDA */}
                <motion.div
                  key="central-console-on"
                  className="absolute inset-0"
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity:
                      coreBootStage === 'awakening'
                        ? [0, 0.12, 0, 0.42, 0.14, 1, 0.78]
                        : coreBootStage === 'companion' ||
                            coreBootStage === 'complete' ||
                            consoleBootFinished ||
                            roomPhase === 'core_active' ||
                            roomPhase === 'growth' ||
                            roomPhase === 'conservation' ||
                            roomPhase === 'awakening' ||
                            roomPhase === 'awakened'
                          ? [0.72, 0.84, 0.73, 0.82, 0.72]
                          : 0,
                  }}
                  transition={
                    coreBootStage === 'awakening'
                      ? {
                          opacity: {
                            duration: 2.4,
                            times: [0, 0.12, 0.24, 0.42, 0.56, 0.82, 1],
                            ease: 'easeInOut',
                          },
                        }
                      : coreBootStage === 'companion' ||
                          coreBootStage === 'complete' ||
                          consoleBootFinished ||
                          roomPhase === 'core_active' ||
                          roomPhase === 'growth' ||
                          roomPhase === 'conservation' ||
                          roomPhase === 'awakening' ||
                          roomPhase === 'awakened'
                        ? {
                            opacity: {
                              duration: 8,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            },
                          }
                        : {
                            opacity: {
                              duration: 0.65,
                              ease: [0.22, 1, 0.36, 1],
                            },
                          }
                  }
                >
                  <Image
                    src={AIA_BACKGROUND_ON}
                    alt=""
                    fill
                    priority
                    sizes="1536px"
                    className="object-contain"
                    draggable={false}
                    unoptimized
                  />
                </motion.div>

                <div className="absolute inset-0 bg-black/10" />
              </div>
            </CinematicSceneFrame>
          </motion.div>
        )}

        {isConsoleScreen && (
          <motion.div
            key="aia-wall-background"
            initial={{
              opacity: 0,
              scale: 1.015,
              filter: 'blur(5px)',
            }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
            }}
            exit={{
              opacity: 0,
              scale: 1.035,
              filter: 'blur(5px)',
            }}
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute inset-0 overflow-hidden bg-black"
          >
            <CinematicSceneFrame
              sourceWidth={1536}
              sourceHeight={1024}
              fit="height"
              showEnvironment
            >
              <div className="promanity-scene-image">
                <Image
                  src={CONSOLE_SCREEN_OFF}
                  alt=""
                  fill
                  priority
                  sizes="1536px"
                  className="object-contain"
                  draggable={false}
                  unoptimized
                />

                {/*
                * ROOM ON
                *
                * Estado activo permanente.
                */}

                <motion.div
                  className="absolute inset-0"
                  initial={false}
                  animate={{
                    opacity: showSteadyRoomOn
                      ? 1
                      : 0,
                  }}
                  transition={{
                    opacity: {
                      duration: 1.2,
                      ease: [0.22, 1, 0.36, 1],
                    },
                  }}
                >
                  <Image
                    src={CONSOLE_SCREEN_ON}
                    alt=""
                    fill
                    priority
                    sizes="1536px"
                    className="object-contain"
                    draggable={false}
                    unoptimized
                  />
                </motion.div>
              </div>
            </CinematicSceneFrame>
          </motion.div>
        )}

        {isConsoleFocus && (
          <motion.div
            key="aia-focus-background"
            initial={{
              opacity: 0,
              scale: 1.06,
              filter: 'blur(10px)',
            }}
            animate={{
              opacity: 1,
              scale: 1,
              filter: 'blur(0px)',
            }}
            exit={{
              opacity: 0,
              scale: 1.035,
              filter: 'blur(7px)',
            }}
            transition={{
              duration: 0.7,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute inset-0 overflow-hidden bg-black"
          >
            <CinematicSceneFrame
              sourceWidth={1536}
              sourceHeight={1024}
              fit="height"
            >
              <div className="promanity-scene-image">
                <Image
                  src={CONSOLE_FOCUS}
                  alt=""
                  fill
                  priority
                  sizes="1536px"
                  className="object-contain"
                  draggable={false}
                  unoptimized
                />
              </div>
            </CinematicSceneFrame>
          </motion.div>
        )}

        {isWorkspace && (
          <motion.div
            key="workspace-background"
            initial={{
              opacity: 0,
              scale: 1.025,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              scale: 1.025,
            }}
            transition={{
              duration: 0.65,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="absolute inset-0"
          >
            <Image
              src={WINDOW_BACKGROUND}
              alt=""
              fill
              priority
              sizes="100vw"
              className="object-cover object-center"
              unoptimized
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}